// src/pages/MenuScannerPage.jsx
import React, { useCallback, useMemo, useRef, useState, useEffect } from "react";
import styled from "styled-components";
import { useNavigate } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  FaCamera,
  FaCloudDownloadAlt,
  FaFilePdf,
  FaMagic,
  FaSave,
  FaTrash,
} from "react-icons/fa";

const COLOR = {
  primary: "#015b9a",
  bg: "#f5f7fb",
  card: "#ffffff",
  line: "#e5e7eb",
  text: "#0f172a",
  muted: "#6b7280",
  green: "#16a34a",
  orange: "#f97316",
  red: "#ef4444",
};

const normalizeCategoryName = (name) =>
  String(name || "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, " ");

const normalizePrice = (value) => {
  const cleaned = String(value || "").replace(/[^0-9.]/g, "");
  if (!cleaned) return "";
  const [whole, ...rest] = cleaned.split(".");
  const decimals = rest.join("").slice(0, 2);
  return decimals ? `${whole}.${decimals}` : whole;
};

const normalizeVariantsForItem = (itemId, variants = []) => {
  const list = Array.isArray(variants) ? variants : [];
  return list.map((variant, idx) => ({
    ...variant,
    id: variant?.id ?? `${itemId}-v-${idx}`,
  }));
};

const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const getCategoryLabel = (row) =>
  String(
    row?.productcategoryname ??
      row?.name ??
      row?.categoryname ??
      row?.category_name ??
      ""
  ).trim();

const getCategoryId = (row) =>
  row?.id ??
  row?.productcategoryid ??
  row?.categoryid ??
  row?.category_id ??
  row?.productcategory_id ??
  null;

const MenuScannerPage = () => {
  const navigate = useNavigate();
  const [imagePreview, setImagePreview] = useState(null);
  const [items, setItems] = useState([]);
  const [processingImage, setProcessingImage] = useState(false);
  const [bulkSaving, setBulkSaving] = useState(false);
  const [categories, setCategories] = useState([]);
  const [recognizedCategories, setRecognizedCategories] = useState([]);
  const [categoryMap, setCategoryMap] = useState({});
  const fileInputRef = useRef(null);
  const pdfInputRef = useRef(null);
  const categoryMapRef = useRef({});
  const categoryCreateRef = useRef({});
  const [uploadedFileMeta, setUploadedFileMeta] = useState(null);
  const [saveSummary, setSaveSummary] = useState({ open: false, saved: 0, total: 0 });

  const userData = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem("userData") || "{}").data || null;
    } catch {
      return null;
    }
  }, []);
  const restaurantId = userData?.id;

  const parsedCount = useMemo(() => items.length, [items]);
  const selectedCount = useMemo(
    () => items.filter((it) => it.selected).length,
    [items]
  );
  const allSelected = useMemo(
    () => items.length > 0 && items.every((it) => it.selected),
    [items]
  );

  const fetchCategories = useCallback(async () => {
    if (!restaurantId) return;
    try {
      const res = await fetch(
        "https://qr.billsarthi.in/public/api/list/productcategory",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ restaurantid: restaurantId }),
        }
      );
      const json = await res.json();
      const list = Array.isArray(json?.list)
        ? json.list
        : Array.isArray(json?.categorylist)
        ? json.categorylist
        : Array.isArray(json?.data)
        ? json.data
        : [];
      const map = {};
      list.forEach((c) => {
        const label = getCategoryLabel(c);
        const key = normalizeCategoryName(label);
        if (!key) return;
        map[key] = {
          id: getCategoryId(c),
          name: label,
        };
      });
      setCategories(list);
      setCategoryMap(map);
      categoryMapRef.current = map;
      // Re-bind category IDs on already extracted items (same behavior as mobile).
      setItems((prev) =>
        prev.map((it) => {
          const name = (it.categoryName || it.category || "").trim();
          const key = normalizeCategoryName(name);
          const match = key ? map[key] : null;
          return {
            ...it,
            categoryName: name,
            categoryId: match?.id ?? it.categoryId ?? null,
          };
        })
      );
    } catch (e) {
      toast.error(e?.message || "Failed to load categories");
    }
  }, [restaurantId]);

  const resolveCategoryIdByName = useCallback(
    async (name) => {
      if (!restaurantId) return null;
      const key = normalizeCategoryName(name);
      if (!key) return null;
      const cached = categoryMapRef.current[key];
      if (cached?.id) return cached.id;
      try {
        const res = await fetch(
          "https://qr.billsarthi.in/public/api/list/productcategory",
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ restaurantid: restaurantId }),
          }
        );
        const json = await res.json();
        const list = Array.isArray(json?.list)
          ? json.list
          : Array.isArray(json?.categorylist)
          ? json.categorylist
          : Array.isArray(json?.data)
          ? json.data
          : [];
        for (const row of list) {
          const label = getCategoryLabel(row);
          if (normalizeCategoryName(label) !== key) continue;
          const id = getCategoryId(row);
          if (id) return id;
        }
      } catch {}
      return null;
    },
    [restaurantId]
  );

  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  const attachIds = (arr) =>
    Array.isArray(arr)
      ? arr.map((it, idx) => ({
          ...it,
          id: it?.id ?? `${Date.now()}-${idx}`,
          selected: typeof it?.selected === "boolean" ? it.selected : true,
          status: it?.status || "ready",
          error: it?.error || "",
        }))
      : [];

  const bindCategoryToItems = (list) => {
    return list.map((it) => {
      const name = (it.categoryName || it.category || "").trim();
      const key = normalizeCategoryName(name);
      const match = key ? categoryMapRef.current[key] : null;
      return {
        ...it,
        categoryName: name,
        categoryId: match?.id ?? it.categoryId ?? null,
      };
    });
  };

  const updateItem = (id, updates) => {
    setItems((prev) =>
      prev.map((it) => {
        if (it.id !== id) return it;
        const next = { ...it, ...updates };
        if (Object.prototype.hasOwnProperty.call(updates, "categoryName")) {
          const key = normalizeCategoryName(updates.categoryName || "");
          const match = key ? categoryMapRef.current[key] : null;
          next.categoryId = match?.id || null;
        }
        return next;
      })
    );
  };

  const updateVariant = (itemId, variantId, updates) => {
    setItems((prev) =>
      prev.map((item) => {
        if (item.id !== itemId) return item;
        const variants = Array.isArray(item.variants) ? item.variants : [];
        const nextVariants = variants.map((variant) =>
          variant.id === variantId ? { ...variant, ...updates } : variant
        );
        const selected = nextVariants.find((v) => v.id === item.defaultVariantId);
        return {
          ...item,
          variants: nextVariants,
          price: selected?.price ?? item.price,
        };
      })
    );
  };

  const setDefaultVariant = (itemId, variantId) => {
    setItems((prev) =>
      prev.map((item) => {
        if (item.id !== itemId) return item;
        const variants = Array.isArray(item.variants) ? item.variants : [];
        const selected = variants.find((v) => v.id === variantId);
        return {
          ...item,
          defaultVariantId: variantId,
          price: selected?.price ?? item.price,
        };
      })
    );
  };

  const ensureCategory = async (name) => {
    const trimmed = (name || "").trim();
    if (!trimmed) return null;
    const key = normalizeCategoryName(trimmed);
    const cached = categoryMapRef.current[key];
    if (cached?.id) return cached.id;
    if (categoryCreateRef.current[key]) return categoryCreateRef.current[key];

    const promise = (async () => {
      const res = await fetch(
        "https://qr.billsarthi.in/public/api/add/addproductcategory",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            restaurantid: restaurantId,
            name: trimmed,
            productcategoryname: trimmed,
          }),
        }
      );
      const json = await res.json();
      if (!res.ok || (!json?.success && json?.status !== true)) {
        throw new Error(json?.message || "Failed to create category");
      }
      const directId =
        json?.id ??
        json?.categoryid ??
        json?.category_id ??
        json?.productcategoryid ??
        json?.data?.id ??
        null;
      if (directId) return directId;
      await fetchCategories();
      const updated = categoryMapRef.current[key];
      if (updated?.id) return updated.id;
      return await resolveCategoryIdByName(trimmed);
    })();

    categoryCreateRef.current[key] = promise;
    try {
      return await promise;
    } finally {
      delete categoryCreateRef.current[key];
    }
  };

  const processUploadFile = async (f) => {
    if (!f) return;
    setUploadedFileMeta({ name: f.name, type: f.type || "" });
    if ((f.type || "").startsWith("image/")) {
      const url = URL.createObjectURL(f);
      setImagePreview(url);
    } else {
      setImagePreview(null);
    }

    try {
      setProcessingImage(true);
      const base64 = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(f);
      });
      const encoded = String(base64).split(",")[1] || "";

      let result = null;
      let responseOk = false;
      let lastMessage = "AI extraction failed";
      const maxAttempts = 3;

      for (let attempt = 1; attempt <= maxAttempts; attempt += 1) {
        const response = await fetch(
          "https://qr.billsarthi.in/public/api/menu-scanner-v2/extract",
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              image: encoded,
              mime_type: f.type || "image/jpeg",
              restaurantid: restaurantId || null,
            }),
          }
        );
        const json = await response.json().catch(() => ({}));
        result = json;
        responseOk = response.ok && !!json?.success;
        lastMessage = json?.message || lastMessage;
        if (responseOk) break;

        const msg = String(json?.message || "").toLowerCase();
        const retriable =
          response.status >= 500 ||
          msg.includes("unavailable") ||
          msg.includes("timeout") ||
          msg.includes("temporar");
        if (!retriable || attempt === maxAttempts) break;
        await wait(600 * attempt);
      }

      if (!responseOk || !result?.success) {
        throw new Error(lastMessage || "AI extraction failed");
      }

      const parsed = result?.data || {};
      setRecognizedCategories(parsed.categories || []);

      if (Array.isArray(parsed.categories) && parsed.categories.length) {
        const nextMap = { ...categoryMapRef.current };
        parsed.categories.forEach((name) => {
          const key = normalizeCategoryName(name);
          if (!nextMap[key]) nextMap[key] = { id: null, name };
        });
        categoryMapRef.current = nextMap;
        setCategoryMap(nextMap);
      }

      const rawItems = Array.isArray(parsed.items) ? parsed.items : [];
      const normalizedItems = rawItems.map((it, idx) => {
        const itemId = String(it?.id ?? `${Date.now()}-${idx}`);
        const variants = normalizeVariantsForItem(itemId, it?.variants);
        return {
          id: itemId,
          name: it?.name || it?.productname || "",
          price: normalizePrice(it?.price ?? it?.rate ?? ""),
          categoryName: it?.category || it?.categoryName || "",
          categoryId: it?.categoryId || null,
          variants,
          defaultVariantId: it?.defaultVariantId || variants[0]?.id || null,
        };
      });

      const withIds = attachIds(normalizedItems);
      setItems(bindCategoryToItems(withIds));
      toast.success(`Extracted ${normalizedItems.length} items`);
    } catch (error) {
      toast.error(error?.message || "Image processing failed");
    } finally {
      setProcessingImage(false);
    }
  };

  const handleImageFile = async (e) => {
    const f = e.target.files?.[0];
    await processUploadFile(f);
    if (e.target) e.target.value = "";
  };

  const handlePdfFile = async (e) => {
    const f = e.target.files?.[0];
    await processUploadFile(f);
    if (e.target) e.target.value = "";
  };

  const saveSelected = async () => {
    if (!restaurantId) {
      toast.error("Missing restaurant information");
      return;
    }
    const selected = items.filter((it) => it.selected);
    if (!selected.length) {
      toast.info("Select at least one item to save");
      return;
    }
    setBulkSaving(true);

    const prepared = [];
    for (const item of selected) {
      const name = (item.name || "").trim();
      const rawCat = (item.categoryName || "").trim();
      if (!name) {
        updateItem(item.id, { status: "error", error: "Name required" });
        continue;
      }
      const variants = Array.isArray(item.variants) ? item.variants : [];
      const sanitizedVariants = variants
        .map((variant, idx) => ({
          ...variant,
          id: variant.id ?? `${item.id}-v-${idx}`,
          label: String(variant?.label || "").trim(),
          price: normalizePrice(String(variant?.price ?? "")),
          _index: idx,
        }))
        .filter((variant) => variant.label && variant.price);
      const hasVariants = sanitizedVariants.length > 0;
      const defaultVariant =
        sanitizedVariants.find((v) => v.id === item.defaultVariantId) ||
        sanitizedVariants.find((v) => v.is_default || v.isDefault) ||
        sanitizedVariants[0];

      const basePrice = normalizePrice(
        hasVariants ? defaultVariant?.price : item.price
      );
      if (!basePrice || Number(basePrice) <= 0) {
        updateItem(item.id, { status: "error", error: "Price required" });
        continue;
      }

      if (!rawCat) {
        updateItem(item.id, { status: "error", error: "Category required" });
        continue;
      }

      let categoryId = item.categoryId;
      try {
        categoryId =
          categoryId ||
          (await resolveCategoryIdByName(rawCat)) ||
          (await ensureCategory(rawCat));
      } catch (err) {
        updateItem(item.id, { status: "error", error: err?.message || "Category error" });
        continue;
      }
      if (!categoryId) {
        updateItem(item.id, {
          status: "error",
          error: "Category not mapped. Please re-select category and try again.",
        });
        continue;
      }

      const normalizedAmount = Number(Number(basePrice).toFixed(2));
      const defaultVariantIndex = hasVariants
        ? Math.max(
            0,
            sanitizedVariants.findIndex((v) => v.id === defaultVariant?.id)
          )
        : 0;

      prepared.push({
        item,
        payload: {
          productcategoryid: categoryId,
          name,
          price: normalizedAmount,
          gstenable: 0,
          gstpercetage: 0,
          withorwithoutgst: "withoutgst",
          priceaftertax: normalizedAmount,
          taxamount: 0,
          sgst: 0,
          cgst: 0,
          size: "Full",
          variants: hasVariants
            ? sanitizedVariants.map((variant, idx) => ({
                label: variant.label,
                price: Number(variant.price),
                is_default: idx === defaultVariantIndex,
              }))
            : [],
          default_variant_index: hasVariants ? defaultVariantIndex : null,
          default_variant_label: hasVariants ? defaultVariant?.label || null : null,
        },
      });

      updateItem(item.id, { status: "saving", error: "", categoryId });
    }

    if (!prepared.length) {
      setBulkSaving(false);
      toast.error("No valid items to save");
      return;
    }

    try {
      const response = await fetch(
        "https://qr.billsarthi.in/public/api/menu-scanner-v2/bulk-add",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            restaurantid: restaurantId,
            items: prepared.map(({ item, payload }) => ({
              ...payload,
              client_id: item.id,
            })),
          }),
        }
      );
      const result = await response.json();
      const resultMap = new Map();
      if (Array.isArray(result?.results)) {
        result.results.forEach((entry) => {
          if (entry?.client_id !== undefined && entry?.client_id !== null) {
            resultMap.set(String(entry.client_id), entry);
          }
        });
      }

      // If bulk fails (or gives unusable result), retry item-by-item so valid items still save.
      const shouldRetryIndividually = !response.ok || resultMap.size === 0;

      if (shouldRetryIndividually) {
        let savedCount = 0;
        let failCount = 0;
        for (const { item, payload } of prepared) {
          try {
            const singleRes = await fetch(
              "https://qr.billsarthi.in/public/api/menu-scanner-v2/bulk-add",
              {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                  restaurantid: restaurantId,
                  items: [{ ...payload, client_id: item.id }],
                }),
              }
            );
            const singleJson = await singleRes.json().catch(() => ({}));
            const entry =
              Array.isArray(singleJson?.results) && singleJson.results.length
                ? singleJson.results[0]
                : null;

            if (singleRes.ok && entry?.status === "saved") {
              updateItem(item.id, { status: "saved", error: "" });
              savedCount += 1;
            } else {
              const msg =
                (singleJson?.errors &&
                  Object.values(singleJson.errors).flat().join("\n")) ||
                entry?.message ||
                singleJson?.message ||
                "Failed to save this item";
              updateItem(item.id, { status: "error", error: msg });
              failCount += 1;
            }
          } catch (e) {
            updateItem(item.id, {
              status: "error",
              error: e?.message || "Network error",
            });
            failCount += 1;
          }
        }

        if (savedCount > 0) {
          toast.success(
            failCount > 0
              ? `${savedCount} items saved, ${failCount} failed`
              : "Menu items saved"
          );
          setSaveSummary({
            open: true,
            saved: savedCount,
            total: prepared.length,
          });
        } else {
          const bulkMsg =
            (result?.errors && Object.values(result.errors).flat().join("\n")) ||
            result?.message ||
            "Failed to save items";
          toast.error(bulkMsg);
        }
        return;
      }

      let savedCount = 0;
      prepared.forEach(({ item }) => {
        const entry = resultMap.get(String(item.id));
        if (entry?.status === "saved") {
          updateItem(item.id, { status: "saved", error: "" });
          savedCount += 1;
        } else {
          updateItem(item.id, {
            status: "error",
            error: entry?.message || result?.message || "Failed to add product",
          });
        }
      });

      toast.success(
        savedCount === prepared.length
          ? "Menu items saved"
          : `${savedCount} items saved`
      );
      if (savedCount > 0) {
        setSaveSummary({
          open: true,
          saved: savedCount,
          total: prepared.length,
        });
      }
    } catch (error) {
      prepared.forEach(({ item }) =>
        updateItem(item.id, { status: "error", error: error?.message || "Network error" })
      );
      toast.error(error?.message || "Save failed");
    } finally {
      setBulkSaving(false);
    }
  };

  const exportJson = () => {
    if (!items.length) return toast.info("No items to export");
    const payload = items.map(({ name, price, categoryName, variants }) => ({
      name,
      price,
      category: categoryName || undefined,
      variants,
    }));
    const blob = new Blob([JSON.stringify(payload, null, 2)], {
      type: "application/json",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "menu-items.json";
    a.click();
    URL.revokeObjectURL(url);
    toast.success("Exported JSON");
  };

  const clearAll = () => {
    setItems([]);
    setImagePreview(null);
    setUploadedFileMeta(null);
    setRecognizedCategories([]);
  };

  const toggleSelectAll = (checked) => {
    setItems((prev) => prev.map((it) => ({ ...it, selected: checked })));
  };

  return (
    <Page>
      <ToastContainer position="top-right" autoClose={2200} />
      <Top>
        <TopContent>
          <Title>
            <FaMagic /> Menu Scanner (AI Assist)
          </Title>
          <Sub>
            Upload a menu photo and auto-extract items & prices. Edit and save directly.
          </Sub>
          <Hints>
            <li>Image/PDF upload uses AI extraction (same as mobile).</li>
          </Hints>
          <TopStats>
            <MetricChip>
              <span>Extracted</span>
              <strong>{parsedCount}</strong>
            </MetricChip>
            <MetricChip>
              <span>Selected</span>
              <strong>{selectedCount}</strong>
            </MetricChip>
            <MetricChip>
              <span>Categories</span>
              <strong>{categories.length}</strong>
            </MetricChip>
          </TopStats>
        </TopContent>
        <ActionRow>
          <PrimaryBtn onClick={() => fileInputRef.current?.click()}>
            <FaCamera /> Upload Image
          </PrimaryBtn>
          <PrimaryBtn onClick={() => pdfInputRef.current?.click()}>
            <FaFilePdf /> Upload PDF
          </PrimaryBtn>
          <PrimaryBtn onClick={exportJson} disabled={!items.length}>
            <FaCloudDownloadAlt /> Export JSON
          </PrimaryBtn>
          <GhostBtn onClick={clearAll}>
            <FaTrash /> Clear
          </GhostBtn>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            style={{ display: "none" }}
            onChange={handleImageFile}
          />
          <input
            ref={pdfInputRef}
            type="file"
            accept="application/pdf,.pdf"
            style={{ display: "none" }}
            onChange={handlePdfFile}
          />
        </ActionRow>
      </Top>

      {processingImage ? (
        <ProcessingCard>
          <Spinner />
          <div>
            <strong>Analyzing menu…</strong>
            <div>Extracting items and categories</div>
          </div>
        </ProcessingCard>
      ) : null}

      <Grid>
        <Card>
          <CardHead>
            <CardTitle>Menu Image</CardTitle>
            {imagePreview && <Chip>Preview loaded</Chip>}
            {!imagePreview && uploadedFileMeta?.type === "application/pdf" ? (
              <Chip>{uploadedFileMeta.name}</Chip>
            ) : null}
          </CardHead>
          <ImageBox>
            {imagePreview ? (
              <img src={imagePreview} alt="menu" />
            ) : uploadedFileMeta?.type === "application/pdf" ? (
              <Empty>PDF selected: {uploadedFileMeta.name}</Empty>
            ) : (
              <Empty>Upload a menu photo for reference.</Empty>
            )}
          </ImageBox>
          {recognizedCategories.length ? (
            <CategoryChips>
              {recognizedCategories.map((c) => (
                <Chip key={c} $tone="info">{c}</Chip>
              ))}
            </CategoryChips>
          ) : null}
        </Card>

      </Grid>

      <Card>
        <CardHead>
          <CardTitle>
            Extracted Items {parsedCount ? `(${parsedCount})` : ""}
          </CardTitle>
          <ExtractedActions>
            <SelectAllLabel>
              <input
                type="checkbox"
                checked={allSelected}
                onChange={(e) => toggleSelectAll(e.target.checked)}
                disabled={!items.length}
              />
              Select All
            </SelectAllLabel>
            <PrimaryBtn onClick={saveSelected} disabled={!items.length || bulkSaving}>
              <FaSave /> {bulkSaving ? "Saving…" : "Save Selected"}
            </PrimaryBtn>
          </ExtractedActions>
        </CardHead>
        {items.length === 0 ? (
          <Empty>Nothing yet. Upload an image or PDF to extract items.</Empty>
        ) : (
          <ItemsGrid>
            {items.map((it) => (
              <ItemCard key={it.id}>
                <ItemRow>
                  <ItemLabel>
                    <input
                      type="checkbox"
                      checked={it.selected}
                      onChange={(e) => updateItem(it.id, { selected: e.target.checked })}
                    />
                    Name
                  </ItemLabel>
                  <Input
                    value={it.name}
                    onChange={(e) => updateItem(it.id, { name: e.target.value })}
                  />
                </ItemRow>
                <ItemRow>
                  <ItemLabel>Price</ItemLabel>
                  <Input
                    value={it.price}
                    onChange={(e) =>
                      updateItem(it.id, {
                        price: e.target.value.replace(/[^\d.]/g, ""),
                      })
                    }
                  />
                </ItemRow>
                <ItemRow>
                  <ItemLabel>Category</ItemLabel>
                  <Input
                    list="category-list"
                    value={it.categoryName || ""}
                    onChange={(e) => updateItem(it.id, { categoryName: e.target.value })}
                    placeholder="Starters / Drinks / Main Course"
                  />
                </ItemRow>

                {Array.isArray(it.variants) && it.variants.length ? (
                  <VariantBlock>
                    <VariantTitle>Variants</VariantTitle>
                    {it.variants.map((variant, vIdx) => (
                      <VariantRow key={variant.id || `${it.id}-v-${vIdx}`}>
                        <input
                          type="radio"
                          name={`default-${it.id}`}
                          checked={it.defaultVariantId === variant.id}
                          onChange={() => setDefaultVariant(it.id, variant.id)}
                        />
                        <Input
                          value={variant.label || ""}
                          onChange={(e) =>
                            updateVariant(it.id, variant.id, { label: e.target.value })
                          }
                          placeholder="Label"
                        />
                        <Input
                          value={variant.price || ""}
                          onChange={(e) =>
                            updateVariant(it.id, variant.id, {
                              price: e.target.value.replace(/[^\d.]/g, ""),
                            })
                          }
                          placeholder="Price"
                        />
                      </VariantRow>
                    ))}
                  </VariantBlock>
                ) : null}

                <ItemActions>
                  <StatusPill $tone={it.status}>{it.status}</StatusPill>
                  {it.error ? <ErrorText>{it.error}</ErrorText> : null}
                </ItemActions>
              </ItemCard>
            ))}
          </ItemsGrid>
        )}
      </Card>

      <datalist id="category-list">
        {categories.map((c) => (
          <option key={c.id} value={c.productcategoryname || c.name} />
        ))}
      </datalist>

      {saveSummary.open ? (
        <ModalOverlay>
          <ModalCard>
            <ModalTitle>Products Imported Successfully</ModalTitle>
            <ModalText>
              {saveSummary.saved} out of {saveSummary.total} selected products were saved.
              You can now review them on the Products page.
            </ModalText>
            <ModalActions>
              <ModalPrimary
                type="button"
                onClick={() => {
                  setSaveSummary({ open: false, saved: 0, total: 0 });
                  navigate("/add-product");
                }}
              >
                Go to Products
              </ModalPrimary>
              <ModalGhost
                type="button"
                onClick={() => setSaveSummary({ open: false, saved: 0, total: 0 })}
              >
                Close
              </ModalGhost>
            </ModalActions>
          </ModalCard>
        </ModalOverlay>
      ) : null}
    </Page>
  );
};

export default MenuScannerPage;

/* ---------------- styled ---------------- */
const Page = styled.div`
  background:
    radial-gradient(720px 360px at 8% -8%, rgba(1, 91, 154, 0.16), transparent 60%),
    radial-gradient(620px 300px at 100% 0%, rgba(56, 189, 248, 0.14), transparent 52%),
    ${COLOR.bg};
  min-height: 100vh;
  padding: 18px;
`;
const Top = styled.div`
  display: grid;
  gap: 10px;
  margin-bottom: 14px;
`;
const TopContent = styled.div`
  min-width: 0;
`;
const TopStats = styled.div`
  margin-top: 10px;
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;
const MetricChip = styled.div`
  background: rgba(255, 255, 255, 0.85);
  border: 1px solid rgba(148, 163, 184, 0.3);
  border-radius: 12px;
  padding: 8px 10px;
  min-width: 110px;
  display: grid;
  gap: 2px;

  span {
    color: ${COLOR.muted};
    font-size: 11px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }
  strong {
    color: ${COLOR.text};
    font-size: 15px;
  }
`;
const Title = styled.h1`
  margin: 0;
  font-size: 1.35rem;
  font-weight: 900;
  color: ${COLOR.text};
  display: flex;
  gap: 8px;
  align-items: center;
`;
const Sub = styled.div`
  color: ${COLOR.muted};
  margin-top: 4px;
  font-weight: 600;
`;
const Hints = styled.ul`
  margin: 8px 0 0;
  padding-left: 18px;
  color: ${COLOR.muted};
  font-size: 13px;
  li {
    margin-bottom: 3px;
  }
`;
const ActionRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;
const PrimaryBtn = styled.button`
  background: ${(p) =>
    p.disabled ? "rgba(148,163,184,0.35)" : "linear-gradient(135deg,#06b6d4,#0ea5e9)"};
  color: #ffffff;
  border: 1px solid ${(p) => (p.disabled ? "transparent" : "rgba(2,132,199,.35)")};
  border-radius: 11px;
  padding: 11px 14px;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-weight: 800;
  cursor: pointer;
  box-shadow: ${(p) => (p.disabled ? "none" : "0 10px 16px rgba(34,211,238,.22)")};
  transition: transform 0.16s ease;
  justify-content: flex-start;
  &:hover {
    transform: ${(p) => (p.disabled ? "none" : "translateY(-1px)")};
  }
`;
const GhostBtn = styled.button`
  background: #fff;
  border: 1px solid ${COLOR.line};
  color: ${COLOR.text};
  border-radius: 11px;
  padding: 11px 14px;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-weight: 800;
  justify-content: flex-start;
  cursor: pointer;
`;
const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
  gap: 14px;
  margin-bottom: 14px;
`;
const Card = styled.div`
  background: rgba(255, 255, 255, 0.94);
  border: 1px solid rgba(148, 163, 184, 0.3);
  border-radius: 14px;
  padding: 14px;
  box-shadow: 0 12px 24px rgba(15, 23, 42, 0.08);
`;
const CardHead = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 8px;
  margin-bottom: 10px;
`;
const ExtractedActions = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
`;
const SelectAllLabel = styled.label`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  color: ${COLOR.text};
  font-weight: 700;
  font-size: 0.92rem;
`;
const CardTitle = styled.div`
  font-weight: 800;
  color: ${COLOR.text};
  font-size: 1.02rem;
`;
const ImageBox = styled.div`
  background: linear-gradient(145deg, #f8fbff, #f1f5f9);
  border: 1px dashed #94a3b8;
  border-radius: 12px;
  height: 260px;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
  }
`;
const Empty = styled.div`
  color: ${COLOR.muted};
  font-size: 13px;
  text-align: center;
`;
const ItemsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(320px, 1fr));
  gap: 14px;
  align-items: start;

  @media (max-width: 1080px) {
    grid-template-columns: 1fr;
  }
`;
const ItemCard = styled.div`
  border: 1px solid rgba(148, 163, 184, 0.3);
  border-radius: 12px;
  padding: 14px;
  background: linear-gradient(180deg, #ffffff, #f8fafc);
  display: grid;
  gap: 12px;
`;
const ItemRow = styled.div`
  display: grid;
  grid-template-columns: 112px 1fr;
  gap: 10px;
  align-items: center;

  @media (max-width: 560px) {
    grid-template-columns: 1fr;
    gap: 6px;
  }
`;
const ItemLabel = styled.label`
  font-weight: 700;
  color: ${COLOR.text};
  display: flex;
  align-items: center;
  gap: 6px;
`;
const Input = styled.input`
  border: 1px solid #cbd5e1;
  border-radius: 9px;
  padding: 9px 10px;
  font-size: 14px;
`;
const ItemActions = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
`;
const Chip = styled.span`
  background: ${(p) => (p.$tone === "info" ? "#e0f2fe" : "#f1f5f9")};
  color: ${(p) => (p.$tone === "info" ? COLOR.primary : COLOR.text)};
  padding: 4px 10px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 700;
`;
const StatusPill = styled.span`
  padding: 4px 10px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 800;
  color: #fff;
  background: ${(p) =>
    p.$tone === "saved"
      ? COLOR.green
      : p.$tone === "error"
      ? COLOR.red
      : p.$tone === "saving"
      ? COLOR.orange
      : "#94a3b8"};
`;
const ErrorText = styled.span`
  font-size: 12px;
  color: ${COLOR.red};
`;
const ProcessingCard = styled.div`
  background: linear-gradient(145deg, #ffffff, #ecfeff);
  border: 1px solid #bae6fd;
  border-radius: 12px;
  padding: 14px;
  margin-bottom: 14px;
  display: flex;
  align-items: center;
  gap: 12px;
  font-weight: 700;
`;
const Spinner = styled.div`
  width: 28px;
  height: 28px;
  border: 3px solid #dbeafe;
  border-top-color: ${COLOR.primary};
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`;
const VariantBlock = styled.div`
  border: 1px dashed ${COLOR.line};
  border-radius: 10px;
  padding: 10px;
  display: grid;
  gap: 8px;
`;
const VariantTitle = styled.div`
  font-weight: 700;
  color: ${COLOR.text};
`;
const VariantRow = styled.div`
  display: grid;
  grid-template-columns: 24px 1fr 120px;
  gap: 8px;
  align-items: center;

  @media (max-width: 560px) {
    grid-template-columns: 20px 1fr;
  }
`;
const CategoryChips = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 10px;
`;
const ModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(2, 6, 23, 0.58);
  backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 3000;
  padding: 20px;
`;
const ModalCard = styled.div`
  width: min(540px, 100%);
  background: #ffffff;
  border: 1px solid rgba(148, 163, 184, 0.35);
  border-radius: 16px;
  padding: 22px 20px;
  box-shadow: 0 30px 65px rgba(2, 6, 23, 0.28);
`;
const ModalTitle = styled.h3`
  margin: 0;
  color: ${COLOR.text};
  font-size: 1.2rem;
  font-weight: 900;
`;
const ModalText = styled.p`
  margin: 10px 0 0;
  color: ${COLOR.muted};
  line-height: 1.5;
  font-weight: 600;
`;
const ModalActions = styled.div`
  margin-top: 18px;
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  flex-wrap: wrap;
`;
const ModalPrimary = styled.button`
  border: 1px solid rgba(2, 132, 199, 0.36);
  background: linear-gradient(135deg, #06b6d4, #0ea5e9);
  color: #fff;
  border-radius: 10px;
  padding: 10px 14px;
  font-weight: 800;
  cursor: pointer;
`;
const ModalGhost = styled.button`
  border: 1px solid ${COLOR.line};
  background: #ffffff;
  color: ${COLOR.text};
  border-radius: 10px;
  padding: 10px 14px;
  font-weight: 800;
  cursor: pointer;
`;
